import { Badge, Button, Modal, Select, Spinner } from '@/components/ui';
import { useCombinaciones } from '@/hooks/useSysadmin';
import type { CambiarCombinacionNtcResponse, EmpresaSysadmin } from '@/services/sysadmin.service';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useState } from 'react';

interface CambiarCombinacionNtcModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresa: EmpresaSysadmin | null;
  cambiarCombinacionNtc: (nit: string, combinacionNtcId: number | null) => Promise<CambiarCombinacionNtcResponse>;
}

export function CambiarCombinacionNtcModal({ isOpen, onClose, empresa, cambiarCombinacionNtc }: CambiarCombinacionNtcModalProps) {
  const { combinaciones, isLoading: loadingCombinaciones } = useCombinaciones();

  const [selectedCombinacionId, setSelectedCombinacionId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);
  const [success, setSuccess] = useState<CambiarCombinacionNtcResponse | null>(null);

  const handleClose = () => {
    setSelectedCombinacionId('');
    setError(null);
    setConfirmStep(false);
    setSuccess(null);
    onClose();
  };

  const handleOpenConfirm = () => {
    setConfirmStep(true);
  };

  const handleSubmit = async () => {
    if (!empresa) return;
    setSaving(true);
    setError(null);
    try {
      const combinacionId = selectedCombinacionId ? parseInt(selectedCombinacionId) : null;
      const res = await cambiarCombinacionNtc(empresa.nitId, combinacionId);
      setSuccess(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cambiar combinación NTC');
      setConfirmStep(false);
    } finally {
      setSaving(false);
    }
  };

  if (!empresa) return null;

  const selectedCombinacion = selectedCombinacionId
    ? combinaciones.find((c) => c.combinacionId.toString() === selectedCombinacionId)
    : null;

  const isNewCombinacion = selectedCombinacionId !== (empresa.combinacionNtcId?.toString() || '');

  // --- Pantalla de éxito ---
  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Combinación NTC Cambiada" size="md">
        <div className="space-y-4 text-center py-4">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
          <p className="text-sm text-gray-600">{success.message}</p>
          <div className="flex justify-center gap-4">
            {success.requisitosCreados > 0 && <Badge variant="green">{success.requisitosCreados} requisitos creados</Badge>}
            {success.asesoriasFinalizadas > 0 && <Badge variant="yellow">{success.asesoriasFinalizadas} asesorías finalizadas</Badge>}
          </div>
          <div className="pt-2">
            <Button onClick={handleClose}>Cerrar</Button>
          </div>
        </div>
      </Modal>
    );
  }

  // --- Paso de confirmación ---
  if (confirmStep) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="⚠️ Confirmar cambio de NTC" size="md">
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="font-medium text-amber-900">Esta acción es destructiva</p>
                <ul className="text-sm text-amber-800 space-y-1 list-disc pl-4">
                  <li>Se finalizarán todas las asesorías en curso</li>
                  <li>Se resetearán todos los estados de requisitos</li>
                  <li>Se crearán nuevos requisitos según la nueva combinación</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Empresa:</span>
              <span className="font-medium text-gray-900">{empresa.nombre}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">NTC actual:</span>
              <span className="text-gray-700">{empresa.combinacionNtc?.nombre || 'Sin combinación'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">NTC nueva:</span>
              <span className="font-medium text-blue-700">{selectedCombinacion?.nombre || 'Sin combinación'}</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setConfirmStep(false)} disabled={saving}>
              Volver
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
              {saving ? 'Procesando...' : 'Confirmar Cambio'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // --- Selección de combinación ---
  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Cambiar NTC — ${empresa.nombre}`} size="md">
      <div className="space-y-4">
        {/* Combinación actual */}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs font-medium text-gray-500 uppercase mb-1">Combinación actual</p>
          {empresa.combinacionNtc ? (
            <Badge variant="blue">{empresa.combinacionNtc.nombre}</Badge>
          ) : (
            <span className="text-gray-400 italic text-sm">Sin combinación asignada</span>
          )}
        </div>

        {/* Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nueva combinación de NTCs</label>
          {loadingCombinaciones ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner size="sm" /> Cargando combinaciones...
            </div>
          ) : (
            <Select
              value={selectedCombinacionId}
              onChange={(e) => setSelectedCombinacionId(e.target.value)}
              options={[
                { value: '', label: '— Sin combinación —' },
                ...combinaciones.map((c) => ({
                  value: c.combinacionId.toString(),
                  label: c.nombre,
                })),
              ]}
            />
          )}
        </div>

        {/* Detalle de la combinación seleccionada */}
        {selectedCombinacion && (
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-800 uppercase mb-2">NTCs incluidas</p>
            <div className="space-y-1">
              {selectedCombinacion.ntcs.map((ntc) => (
                <div key={ntc.ntcId} className="flex justify-between items-center text-sm">
                  <span className="text-blue-900">{ntc.nombre}</span>
                  <Badge variant="blue">{ntc.totalRequisitos} requisitos</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Advertencia */}
        {isNewCombinacion && selectedCombinacionId !== '' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <RefreshCw className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              Cambiar la combinación <strong>finalizará asesorías en curso</strong> y{' '}
              <strong>reseteará todos los estados de requisitos</strong>.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleOpenConfirm} disabled={!isNewCombinacion}>
            Continuar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
